import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

type StreamEvent =
	| { type: 'sources'; data: Array<{ id: string; title: string; category: string | null }> }
	| { type: 'chunk'; data: string }
	| { type: 'done' }
	| { type: 'error'; data: string }

export class UcoastFaqBot extends UcoastEl {
	static htmlSelector = 'ucoast-faq-bot'

	private endpoint!: string
	private form!: HTMLFormElement
	private input!: HTMLInputElement
	private submitBtn!: HTMLButtonElement
	private spinner?: HTMLElement
	private loader?: HTMLElement
	private result!: HTMLElement
	private answer!: HTMLElement
	private error!: HTMLElement
	private examples?: HTMLElement
	private controller: AbortController | null = null

	override connectedCallback() {
		super.connectedCallback()
		this.endpoint = this.dataset.endpoint ?? '/apps/faq-search/query'
		this.form = q.rs<HTMLFormElement>('[data-faq-form]', this)
		this.input = q.rs<HTMLInputElement>('[data-faq-input]', this)
		this.submitBtn = q.rs<HTMLButtonElement>('[data-faq-submit]', this)
		this.spinner = q.os<HTMLElement>('[data-faq-spinner]', this)
		this.loader = q.os<HTMLElement>('[data-faq-loader]', this)
		this.result = q.rs('[data-faq-result]', this)
		this.answer = q.rs('[data-faq-answer]', this)
		this.error = q.rs('[data-faq-error]', this)
		this.examples = q.os<HTMLElement>('[data-faq-examples]', this)

		this.form.addEventListener('submit', this.onSubmit)
		q.ol<HTMLButtonElement>('[data-faq-example]', this)?.forEach((btn) => {
			btn.addEventListener('click', this.onExampleClick)
		})
	}

	override disconnectedCallback() {
		super.disconnectedCallback?.()
		this.controller?.abort()
		this.form?.removeEventListener('submit', this.onSubmit)
	}

	private onExampleClick = (event: MouseEvent) => {
		const btn = q.rct<MouseEvent, HTMLButtonElement>(event)
		this.input.value = btn.textContent?.trim() ?? ''
		this.form.requestSubmit()
	}

	private onSubmit = async (event: SubmitEvent) => {
		event.preventDefault()
		const question = this.input.value.trim()
		if (!question) return

		this.controller?.abort()
		this.controller = new AbortController()

		this.setLoading(true)
		this.error.hidden = true
		this.result.hidden = false
		this.answer.textContent = ''
		if (this.examples) this.examples.hidden = true

		try {
			const response = await fetch(this.endpoint, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/x-ndjson' },
				body: JSON.stringify({ q: question }),
				signal: this.controller.signal,
			})
			if (!response.ok || !response.body) {
				throw new Error(`Request failed: ${response.status}`)
			}
			await this.consumeStream(response.body)
		} catch (err) {
			if ((err as Error).name === 'AbortError') return
			this.error.hidden = false
			this.result.hidden = true
			console.error('[faq-bot]', err)
		} finally {
			this.setLoading(false)
		}
	}

	private async consumeStream(body: ReadableStream<Uint8Array>) {
		const reader = body.getReader()
		const decoder = new TextDecoder()
		let buffer = ''

		while (true) {
			const { value, done } = await reader.read()
			if (done) break
			buffer += decoder.decode(value, { stream: true })

			let newlineIndex: number
			while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
				const line = buffer.slice(0, newlineIndex).trim()
				buffer = buffer.slice(newlineIndex + 1)
				if (!line) continue
				this.handleEvent(JSON.parse(line) as StreamEvent)
			}
		}

		if (buffer.trim()) {
			try {
				this.handleEvent(JSON.parse(buffer.trim()) as StreamEvent)
			} catch {
				/* tail noise, ignore */
			}
		}
	}

	private handleEvent(event: StreamEvent) {
		switch (event.type) {
			case 'sources':
				// Sources received but intentionally not rendered.
				break
			case 'chunk':
				// Hide the loader as soon as content starts arriving — the
				// streaming text itself is the loading affordance from here.
				if (this.loader && !this.loader.hidden) this.loader.hidden = true
				this.answer.textContent = (this.answer.textContent ?? '') + event.data
				break
			case 'error':
				this.error.textContent = event.data
				this.error.hidden = false
				this.result.hidden = true
				break
			case 'done':
				if (this.loader) this.loader.hidden = true
				break
		}
	}

	private setLoading(loading: boolean) {
		this.submitBtn.toggleAttribute('disabled', loading)
		// Use `readonly` on the input rather than `disabled` so it keeps focus
		// during loading. Disabling a focused input transfers focus to <body>,
		// which makes the browser scroll to the top — the "page jumps on
		// Enter" bug.
		this.input.toggleAttribute('readonly', loading)
		if (this.spinner) this.spinner.hidden = !loading
		if (this.loader) this.loader.hidden = !loading
	}
}
